<template>
  <div class="question-page">
    <!-- 查询区域 -->
    <el-card class="search-card">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="题库">
          <el-select v-model="searchForm.bankCode" placeholder="请选择题库" clearable>
            <el-option
                v-for="item in bankOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="题型">
          <el-select v-model="searchForm.questionType" placeholder="请选择题型" clearable>
            <el-option label="单选题" :value="1" />
            <el-option label="多选题" :value="2" />
            <el-option label="判断题" :value="3" />
          </el-select>
        </el-form-item>

        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>

        <el-form-item label="题干">
          <el-input
              v-model="searchForm.keyword"
              placeholder="请输入题干关键字"
              clearable
          />
        </el-form-item>

        <!-- 按钮组：移到最右侧，和筛选条件同一行 -->
        <el-form-item class="search-buttons">
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button type="success" @click="handleExport">导出 Word</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 表格区域 -->
    <el-card class="table-card">
      <div slot="header" class="table-header">
        <span>题库管理</span>
        <div>
          <el-button type="primary" size="small" @click="openAddDialog">新增题目</el-button>
          <el-button size="small" @click="openImportDialog">Word批量导入</el-button>
        </div>
      </div>

      <el-table :data="tableData" border stripe v-loading="loading">
        <el-table-column type="index" label="序号" width="60" />

        <el-table-column prop="bankName" label="题库" min-width="140" />

        <el-table-column label="题型" width="100">
          <template slot-scope="scope">
            {{ formatQuestionType(scope.row.questionType) }}
          </template>
        </el-table-column>

        <el-table-column prop="content" label="题干" min-width="280" show-overflow-tooltip />

        <el-table-column prop="answer" label="答案" width="100" />

        <el-table-column prop="score" label="分值" width="80" />

        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="createTime" label="创建时间" min-width="180" />

        <el-table-column label="操作" width="260" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="openDetailDialog(scope.row.id)">查看</el-button>
            <el-button type="text" @click="openEditDialog(scope.row.id)">编辑</el-button>
            <el-button type="text" @click="handleStatusChange(scope.row)">
              {{ scope.row.status === 1 ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-box">
        <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- 新增题目弹窗 -->
    <el-dialog
        title="新增题目"
        :visible.sync="addDialogVisible"
        width="800px"
        @close="resetAddForm"
    >
      <el-form
          ref="addFormRef"
          :model="addForm"
          :rules="questionRules"
          label-width="100px"
      >
        <el-form-item label="题库" prop="bankCode">
          <el-select v-model="addForm.bankCode" placeholder="请选择题库" @change="autoCalcScore(addForm)">
            <el-option
                v-for="item in bankOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="题型" prop="questionType">
          <el-select v-model="addForm.questionType" placeholder="请选择题型" @change="handleQuestionTypeChange(addForm)">
            <el-option label="单选题" :value="1" />
            <el-option label="多选题" :value="2" />
            <el-option label="判断题" :value="3" />
          </el-select>
        </el-form-item>

        <el-form-item label="题干" prop="content">
          <el-input v-model="addForm.content" type="textarea" :rows="3" />
        </el-form-item>

        <el-form-item label="题干图片">
          <el-upload
              :auto-upload="false"
              :show-file-list="false"
              accept="image/*"
              :on-change="handleAddContentImageChange"
          >
            <el-button size="small" type="primary">上传题干图片</el-button>
          </el-upload>

          <div v-if="addForm.contentImageId" class="image-preview-box">
            <img :src="getImageUrl(addForm.contentImageId)" class="image-preview" />
            <el-button type="text" style="color:red" @click="removeAddContentImage">删除图片</el-button>
          </div>
        </el-form-item>

        <el-form-item label="正确答案" prop="answer">
          <el-input v-model="addForm.answer" placeholder="例如 A / AC / B" />
        </el-form-item>

        <el-form-item label="解析">
          <el-input v-model="addForm.analysis" type="textarea" :rows="3" />
        </el-form-item>

        <el-form-item label="解析图片">
          <el-upload
              :auto-upload="false"
              :show-file-list="false"
              accept="image/*"
              :on-change="handleAddAnalysisImageChange"
          >
            <el-button size="small" type="primary">上传解析图片</el-button>
          </el-upload>

          <div v-if="addForm.analysisImageId" class="image-preview-box">
            <img :src="getImageUrl(addForm.analysisImageId)" class="image-preview" />
            <el-button type="text" style="color:red" @click="removeAddAnalysisImage">删除图片</el-button>
          </div>
        </el-form-item>

        <el-form-item label="分值" prop="score">
          <el-input-number v-model="addForm.score" :min="1" :precision="2" :step="1" />
        </el-form-item>

        <el-divider>选项列表</el-divider>

        <div
            v-for="(item, index) in addForm.optionList"
            :key="'add-' + index"
            class="option-box"
        >
          <el-row :gutter="10">
            <el-col :span="3">
              <el-input v-model="item.optionLabel" placeholder="标签" />
            </el-col>

            <el-col :span="7">
              <el-input v-model="item.optionContent" placeholder="选项内容" />
            </el-col>

            <el-col :span="6">
              <el-upload
                  :auto-upload="false"
                  :show-file-list="false"
                  accept="image/*"
                  :on-change="file => handleAddOptionImageChange(file, index)"
              >
                <el-button size="small" type="primary">上传选项图片</el-button>
              </el-upload>

              <div v-if="item.optionImageId" class="image-preview-box">
                <img :src="getImageUrl(item.optionImageId)" class="option-image-preview" />
                <el-button type="text" style="color:red" @click="removeAddOptionImage(index)">删除图片</el-button>
              </div>
            </el-col>

            <el-col :span="3">
              <el-input-number v-model="item.sortNum" :min="1" />
            </el-col>

            <el-col :span="3">
              <el-button type="text" style="color:red" @click="removeOption(addForm, index)">删除选项</el-button>
            </el-col>
          </el-row>
        </div>

        <el-button type="primary" plain @click="addOption(addForm)">新增选项</el-button>
      </el-form>

      <div slot="footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAddQuestion">确定</el-button>
      </div>
    </el-dialog>

    <!-- 编辑题目弹窗 -->
    <el-dialog
        title="编辑题目"
        :visible.sync="editDialogVisible"
        width="800px"
        @close="resetEditForm"
    >
      <el-form
          ref="editFormRef"
          :model="editForm"
          :rules="questionRules"
          label-width="100px"
      >
        <el-form-item label="题库" prop="bankCode">
          <el-select v-model="editForm.bankCode" placeholder="请选择题库" @change="autoCalcScore(editForm)">
            <el-option
                v-for="item in bankOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="题型" prop="questionType">
          <el-select v-model="editForm.questionType" placeholder="请选择题型" @change="handleQuestionTypeChange(editForm)">
            <el-option label="单选题" :value="1" />
            <el-option label="多选题" :value="2" />
            <el-option label="判断题" :value="3" />
          </el-select>
        </el-form-item>

        <el-form-item label="题干" prop="content">
          <el-input v-model="editForm.content" type="textarea" :rows="3" />
        </el-form-item>

        <el-form-item label="题干图片">
          <el-upload
              :auto-upload="false"
              :show-file-list="false"
              accept="image/*"
              :on-change="handleEditContentImageChange"
          >
            <el-button size="small" type="primary">上传题干图片</el-button>
          </el-upload>

          <div v-if="editForm.contentImageId" class="image-preview-box">
            <img :src="getImageUrl(editForm.contentImageId)" class="image-preview" />
            <el-button type="text" style="color:red" @click="removeEditContentImage">删除图片</el-button>
          </div>
        </el-form-item>

        <el-form-item label="正确答案" prop="answer">
          <el-input v-model="editForm.answer" />
        </el-form-item>

        <el-form-item label="解析">
          <el-input v-model="editForm.analysis" type="textarea" :rows="3" />
        </el-form-item>

        <el-form-item label="解析图片">
          <el-upload
              :auto-upload="false"
              :show-file-list="false"
              accept="image/*"
              :on-change="handleEditAnalysisImageChange"
          >
            <el-button size="small" type="primary">上传解析图片</el-button>
          </el-upload>

          <div v-if="editForm.analysisImageId" class="image-preview-box">
            <img :src="getImageUrl(editForm.analysisImageId)" class="image-preview" />
            <el-button type="text" style="color:red" @click="removeEditAnalysisImage">删除图片</el-button>
          </div>
        </el-form-item>

        <el-form-item label="分值" prop="score">
          <el-input-number v-model="editForm.score" :min="1" :precision="2" :step="1" />
        </el-form-item>

        <el-divider>选项列表</el-divider>

        <div
            v-for="(item, index) in editForm.optionList"
            :key="'edit-' + index"
            class="option-box"
        >
          <el-row :gutter="10">
            <el-col :span="3">
              <el-input v-model="item.optionLabel" placeholder="标签" />
            </el-col>

            <el-col :span="7">
              <el-input v-model="item.optionContent" placeholder="选项内容" />
            </el-col>

            <el-col :span="6">
              <el-upload
                  :auto-upload="false"
                  :show-file-list="false"
                  accept="image/*"
                  :on-change="file => handleEditOptionImageChange(file, index)"
              >
                <el-button size="small" type="primary">上传选项图片</el-button>
              </el-upload>

              <div v-if="item.optionImageId" class="image-preview-box">
                <img :src="getImageUrl(item.optionImageId)" class="option-image-preview" />
                <el-button type="text" style="color:red" @click="removeEditOptionImage(index)">删除图片</el-button>
              </div>
            </el-col>

            <el-col :span="3">
              <el-input-number v-model="item.sortNum" :min="1" />
            </el-col>

            <el-col :span="3">
              <el-button type="text" style="color:red" @click="removeOption(editForm, index)">删除选项</el-button>
            </el-col>
          </el-row>
        </div>

        <el-button type="primary" plain @click="addOption(editForm)">新增选项</el-button>
      </el-form>

      <div slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEditQuestion">确定</el-button>
      </div>
    </el-dialog>

    <!-- 题目详情弹窗 -->
    <el-dialog
        title="题目详情"
        :visible.sync="detailDialogVisible"
        width="800px"
    >
      <div v-if="detailData" class="detail-box">
        <p><strong>题库：</strong>{{ detailData.bankName }}</p>
        <p><strong>题型：</strong>{{ formatQuestionType(detailData.questionType) }}</p>
        <p><strong>题干：</strong>{{ detailData.content }}</p>
        <p><strong>题干图片：</strong></p>
        <div v-if="detailData.contentImageId" class="image-preview-box">
          <img :src="getImageUrl(detailData.contentImageId)" class="image-preview" />
        </div>
        <div v-else>-</div>
        <p><strong>正确答案：</strong>{{ detailData.answer }}</p>
        <p><strong>解析：</strong>{{ detailData.analysis || '-' }}</p>
        <p><strong>解析图片：</strong></p>
        <div v-if="detailData.analysisImageId" class="image-preview-box">
          <img :src="getImageUrl(detailData.analysisImageId)" class="image-preview" />
        </div>
        <div v-else>-</div>
        <p><strong>分值：</strong>{{ detailData.score }}</p>
        <p><strong>状态：</strong>{{ detailData.status === 1 ? '启用' : '禁用' }}</p>

        <el-divider>选项列表</el-divider>

        <div v-for="(item, index) in detailData.optionList" :key="'detail-' + index" class="detail-option">
          <p>
            <strong>{{ item.optionLabel }}：</strong>{{ item.optionContent }}
          </p>

          <div v-if="item.optionImageId" class="image-preview-box">
            <img :src="getImageUrl(item.optionImageId)" class="option-image-preview" />
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Word批量导入弹窗 -->
    <el-dialog
        title="Word批量导入题目"
        :visible.sync="importDialogVisible"
        width="650px"
        @close="resetImportForm"
    >
      <el-form :model="importForm" label-width="100px">
        <el-form-item label="导入类型">
          <el-radio-group v-model="importForm.importType">
            <el-radio label="TEXT">非图片题</el-radio>
            <el-radio label="IMAGE">图片题</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="目标题库">
          <el-select v-model="importForm.bankCode" placeholder="请选择题库">
            <el-option
                v-for="item in bankOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Word文件">
          <el-upload
              class="upload-demo"
              :auto-upload="false"
              :limit="1"
              :file-list="[]"
              :on-change="handleFileChange"
              :before-remove="handleFileRemove"
              accept=".docx"
          >
            <el-button size="small" type="primary">选择 .docx 文件</el-button>
            <div slot="tip" class="el-upload__tip">
              仅支持 .docx 格式，且题目内容需符合系统规定格式
            </div>
          </el-upload>

          <div v-if="importForm.file" class="selected-file">
            当前文件：{{ importForm.file.name }}
          </div>
        </el-form-item>

        <el-alert
            v-if="importForm.importType === 'TEXT'"
            title="非图片题格式要求：题号行 + 选项行 + 参考答案行；题目之间用空行分隔。"
            type="info"
            :closable="false"
            show-icon
        />

        <el-alert
            v-if="importForm.importType === 'IMAGE'"
            title="图片题格式要求：每题只放一张题干图片；图片放在题干下方、选项上方；题号行 + 选项行 + 参考答案行。"
            type="warning"
            :closable="false"
            show-icon
        />

        <div v-if="importResult" class="import-result">
          <el-divider>导入结果</el-divider>
          <p>总题数：{{ importResult.totalCount }}</p>
          <p>成功导入：{{ importResult.successCount }}</p>
          <p>重复跳过：{{ importResult.duplicateCount }}</p>
        </div>
      </el-form>

      <div slot="footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitImport">开始导入</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getQuestionList,
  addQuestion,
  updateQuestion,
  getQuestionDetail,
  updateQuestionStatus,
  importQuestions,
  importImageQuestions,
  uploadQuestionImage
} from '@/api/admin/question'
import request from '@/utils/request' // 🔥 你项目真正的请求工具

export default {
  name: 'AdminQuestion',
  data() {
    return {

      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },

      loading: false,
      tableData: [],
      detailData: null,

      searchForm: {
        bankCode: '',
        questionType: null,
        status: null,
        keyword: ''
      },

      bankOptions: [
        { label: '客车题库', value: 'BUS' },
        { label: '客车专业', value: 'BUS_PRO' },
        { label: '放射性题库', value: 'RAD' },
        { label: '危险品基础', value: 'DANGER_BASE' },
        { label: '爆炸品基础', value: 'EXPLOSION_BASE' },
        { label: '剧毒基础', value: 'TOXIC_BASE' },
        { label: '驾驶专业', value: 'DRIVE_PRO' },
        { label: '押运专业', value: 'GUARD_PRO' },
        { label: '装卸专业', value: 'LOAD_PRO' }
      ],

      addDialogVisible: false,
      editDialogVisible: false,
      detailDialogVisible: false,
      importDialogVisible: false,

      addForm: this.getDefaultQuestionForm(),
      editForm: this.getDefaultQuestionForm(),

      importForm: {
        importType: 'TEXT',
        bankCode: '',
        file: null
      },

      importResult: null,

      questionRules: {
        bankCode: [{ required: true, message: '请选择题库', trigger: 'change' }],
        questionType: [{ required: true, message: '请选择题型', trigger: 'change' }],
        content: [{ required: true, message: '请输入题干', trigger: 'blur' }],
        answer: [{ required: true, message: '请输入正确答案', trigger: 'blur' }],
        score: [{ required: true, message: '请输入分值', trigger: 'change' }]
      }
    }
  },

  created() {
    this.loadQuestionList()
  },

  methods: {
    getDefaultQuestionForm() {
      return {
        id: null,
        bankCode: '',
        questionType: null,
        content: '',
        contentImageId: null,
        answer: '',
        analysis: '',
        analysisImageId: null,
        score: 1,
        optionList: [
          { optionLabel: 'A', optionContent: '', optionImageId: null, sortNum: 1 },
          { optionLabel: 'B', optionContent: '', optionImageId: null, sortNum: 2 }
        ]
      }
    },

    formatQuestionType(type) {
      if (type === 1) return '单选题'
      if (type === 2) return '多选题'
      if (type === 3) return '判断题'
      return '-'
    },

    getImageUrl(imageId) {
      if (!imageId) {
        return ''
      }
      return `/api/admin/question/image/${imageId}`
    },

    async loadQuestionList() {
      this.loading = true
      try {
        const params = {
          ...this.searchForm,
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }

        const res = await getQuestionList(params)
        if (res.code === 200) {
          this.tableData = res.data.records || []
          this.pagination.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '获取题目列表失败')
        }
      } catch (e) {
        this.$message.error('获取题目列表失败')
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.pageNum = 1
      this.loadQuestionList()
    },

    handleReset() {
      this.searchForm = {
        bankCode: '',
        questionType: null,
        status: null,
        keyword: ''
      }
      this.pagination.pageNum = 1
      this.loadQuestionList()
    },

    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize
      this.pagination.pageNum = 1
      this.loadQuestionList()
    },

    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum
      this.loadQuestionList()
    },

    openAddDialog() {
      this.addDialogVisible = true
    },

    resetAddForm() {
      this.$nextTick(() => {
        if (this.$refs.addFormRef) {
          this.$refs.addFormRef.resetFields()
        }
        this.addForm = this.getDefaultQuestionForm()
      })
    },

    submitAddQuestion() {
      this.$refs.addFormRef.validate(async valid => {
        if (!valid) return

        try {
          const payload = JSON.parse(JSON.stringify(this.addForm))
          payload.answer = this.normalizeAnswer(payload.answer)

          const res = await addQuestion(payload)
          if (res.code === 200) {
            this.$message.success('新增题目成功')
            this.addDialogVisible = false
            this.loadQuestionList()
          } else {
            this.$message.error(res.message || '新增题目失败')
          }
        } catch (e) {
          this.$message.error('新增题目失败')
        }
      })
    },

    async openEditDialog(id) {
      try {
        const res = await getQuestionDetail(id)
        if (res.code === 200) {
          this.editForm = {
            id: res.data.id,
            bankCode: res.data.bankCode,
            questionType: res.data.questionType,
            content: res.data.content,
            contentImageId: res.data.contentImageId,
            answer: res.data.answer,
            analysis: res.data.analysis || '',
            analysisImageId: res.data.analysisImageId,
            score: res.data.score,
            optionList: (res.data.optionList || []).map(item => ({
              optionLabel: item.optionLabel,
              optionContent: item.optionContent,
              optionImageId: item.optionImageId,
              sortNum: item.sortNum
            }))
          }
          this.editDialogVisible = true
        } else {
          this.$message.error(res.message || '获取题目详情失败')
        }
      } catch (e) {
        this.$message.error('获取题目详情失败')
      }
    },

    resetEditForm() {
      this.$nextTick(() => {
        if (this.$refs.editFormRef) {
          this.$refs.editFormRef.resetFields()
        }
        this.editForm = this.getDefaultQuestionForm()
      })
    },

    submitEditQuestion() {
      this.$refs.editFormRef.validate(async valid => {
        if (!valid) return

        try {
          const payload = JSON.parse(JSON.stringify(this.editForm))
          payload.answer = this.normalizeAnswer(payload.answer)

          const res = await updateQuestion(payload)
          if (res.code === 200) {
            this.$message.success('编辑题目成功')
            this.editDialogVisible = false
            this.loadQuestionList()
          } else {
            this.$message.error(res.message || '编辑题目失败')
          }
        } catch (e) {
          this.$message.error('编辑题目失败')
        }
      })
    },

    async openDetailDialog(id) {
      try {
        const res = await getQuestionDetail(id)
        if (res.code === 200) {
          this.detailData = res.data
          this.detailDialogVisible = true
        } else {
          this.$message.error(res.message || '获取题目详情失败')
        }
      } catch (e) {
        this.$message.error('获取题目详情失败')
      }
    },

    handleStatusChange(row) {
      const targetStatus = row.status === 1 ? 0 : 1
      const actionText = targetStatus === 1 ? '启用' : '禁用'

      this.$confirm(`确定要${actionText}该题目吗？`, '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          const res = await updateQuestionStatus({
            id: row.id,
            status: targetStatus
          })
          if (res.code === 200) {
            this.$message.success(`${actionText}成功`)
            this.loadQuestionList()
          } else {
            this.$message.error(res.message || `${actionText}失败`)
          }
        } catch (e) {
          this.$message.error(`${actionText}失败`)
        }
      }).catch(() => {})
    },

    addOption(form) {
      const nextSort = form.optionList.length + 1
      const nextLabel = String.fromCharCode(64 + nextSort)
      form.optionList.push({
        optionLabel: nextLabel,
        optionContent: '',
        optionImageId: null,
        sortNum: nextSort
      })
    },

    removeOption(form, index) {
      if (form.optionList.length <= 2) {
        this.$message.warning('至少保留两个选项')
        return
      }
      form.optionList.splice(index, 1)
    },

    autoCalcScore(form) {
      if (!form.bankCode || !form.questionType) return;
      if (form.bankCode === 'BUS_PRO') {
        if (form.questionType === 1) form.score = 4;
        else if (form.questionType === 2) form.score = 6;
        else if (form.questionType === 3) form.score = 4;
      } else {
        if (form.questionType === 1) form.score = 1;
        else if (form.questionType === 2) form.score = 2;
        else if (form.questionType === 3) form.score = 1;
      }
    },

    handleQuestionTypeChange(form) {
      if (form.questionType === 3) {
        form.optionList = [
          { optionLabel: 'A', optionContent: '正确', optionImageId: null, sortNum: 1 },
          { optionLabel: 'B', optionContent: '错误', optionImageId: null, sortNum: 2 }
        ]
        if (!form.answer) {
          form.answer = 'A'
        }
      }
      // 触发自动算分
      this.autoCalcScore(form);
    },

    normalizeAnswer(answer) {
      if (!answer) return ''
      return answer.toUpperCase().replaceAll(',', '').replaceAll(' ', '')
    },

    openImportDialog() {
      this.importDialogVisible = true
    },

    resetImportForm() {
      this.importForm = {
        importType: 'TEXT',
        bankCode: '',
        file: null
      }
      this.importResult = null
    },

    handleFileChange(file) {
      if (!file || !file.raw) {
        return
      }

      const fileName = file.name || ''
      if (!fileName.toLowerCase().endsWith('.docx')) {
        this.$message.warning('仅支持 .docx 文件')
        this.importForm.file = null
        return
      }

      this.importForm.file = file.raw
    },

    handleFileRemove() {
      this.importForm.file = null
    },

    async submitImport() {
      if (!this.importForm.bankCode) {
        this.$message.warning('请选择目标题库')
        return
      }

      if (!this.importForm.file) {
        this.$message.warning('请选择要上传的 Word 文件')
        return
      }

      const formData = new FormData()
      formData.append('bankCode', this.importForm.bankCode)
      formData.append('file', this.importForm.file)

      try {
        let res
        if (this.importForm.importType === 'IMAGE') {
          res = await importImageQuestions(formData)
        } else {
          res = await importQuestions(formData)
        }

        if (res.code === 200) {
          this.importResult = res.data
          this.$message.success('导入完成')
          this.loadQuestionList()
        } else {
          this.$message.error(res.message || '导入失败')
        }
      } catch (e) {
        console.error('Word导入失败', e)
        this.$message.error('导入失败')
      }
    },
    async uploadImage(file, targetForm, fieldName) {
      const formData = new FormData()
      formData.append('file', file)

      try {
        const res = await uploadQuestionImage(formData)
        if (res.code === 200) {
          targetForm[fieldName] = res.data
          this.$message.success('图片上传成功')
        } else {
          this.$message.error(res.message || '图片上传失败')
        }
      } catch (e) {
        this.$message.error('图片上传失败')
      }
    },

    handleAddContentImageChange(file) {
      this.uploadImage(file.raw, this.addForm, 'contentImageId')
    },

    handleAddAnalysisImageChange(file) {
      this.uploadImage(file.raw, this.addForm, 'analysisImageId')
    },

    handleEditContentImageChange(file) {
      this.uploadImage(file.raw, this.editForm, 'contentImageId')
    },

    handleEditAnalysisImageChange(file) {
      this.uploadImage(file.raw, this.editForm, 'analysisImageId')
    },

    removeAddContentImage() {
      this.addForm.contentImageId = null
    },

    removeAddAnalysisImage() {
      this.addForm.analysisImageId = null
    },

    removeEditContentImage() {
      this.editForm.contentImageId = null
    },

    removeEditAnalysisImage() {
      this.editForm.analysisImageId = null
    },

    handleAddOptionImageChange(file, index) {
      this.uploadOptionImage(file.raw, this.addForm, index)
    },

    handleEditOptionImageChange(file, index) {
      this.uploadOptionImage(file.raw, this.editForm, index)
    },

    async uploadOptionImage(file, targetForm, index) {
      const formData = new FormData()
      formData.append('file', file)

      try {
        const res = await uploadQuestionImage(formData)
        if (res.code === 200) {
          targetForm.optionList[index].optionImageId = res.data
          this.$message.success('选项图片上传成功')
        } else {
          this.$message.error(res.message || '选项图片上传失败')
        }
      } catch (e) {
        this.$message.error('选项图片上传失败')
      }
    },

    removeAddOptionImage(index) {
      this.addForm.optionList[index].optionImageId = null
    },

    removeEditOptionImage(index) {
      this.editForm.optionList[index].optionImageId = null
    },

    // 生成导出文件名
    generateExportFileName() {
      const parts = [];
      if (this.searchForm.bankCode) {
        const bank = this.bankOptions.find(item => item.value === this.searchForm.bankCode);
        if (bank) parts.push(bank.label);
      }
      if (this.searchForm.questionType) {
        parts.push(this.formatQuestionType(this.searchForm.questionType));
      }
      if (this.searchForm.status !== null) {
        parts.push(this.searchForm.status === 1 ? '启用' : '禁用');
      }
      if (this.searchForm.keyword) {
        parts.push(`含${this.searchForm.keyword}`);
      }
      return parts.length > 0 ? `${parts.join('_')}_导出题目.docx` : '全部题目_导出.docx';
    },

// 🔥 最终修复版：URL完全匹配后端，彻底解决404
    async handleExport() {
      this.loading = true;
      try {
        const fileName = this.generateExportFileName();

        // 关键：补全完整接口路径，和后端Controller完全一致
        const res = await request({
          url: '/api/admin/question/export',
          method: 'get',
          params: this.searchForm,
          responseType: 'blob',
          timeout: 15000
        });

        const blob = new Blob([res], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        this.$message.success('导出成功！');
      } catch (err) {
        console.error('导出错误详情：', err);
        this.$message.error('导出失败');
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.pagination-box {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.option-image-preview {
  width: 120px;
  max-width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  margin-top: 6px;
}

.image-preview-box {
  margin-top: 10px;
}

.image-preview {
  width: 200px;
  max-width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.selected-file {
  margin-top: 8px;
  color: #606266;
  font-size: 14px;
}

.import-result {
  margin-top: 15px;
}

.selected-file {
  margin-top: 8px;
  color: #606266;
  font-size: 14px;
}

.import-result {
  margin-top: 15px;
}

.question-page {
  width: 100%;
}

.search-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.option-box {
  margin-bottom: 12px;
}

.detail-box p {
  margin: 8px 0;
}

.detail-option {
  margin-bottom: 8px;
}
</style>